import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ChecklistItem } from '@peles/shared';
import { useThreadCounts, type ThreadCount } from '../../hooks/useThreadCounts';
import { RecordConversations } from '../messaging/RecordConversations';

/** A task as a conversation record — `THREAD_ENTITY_TYPES`' spelling. */
export const TASK_THREAD_ENTITY = 'ChecklistItem';

interface TaskCounts {
  countFor: (taskId: string) => ThreadCount | null;
  /** A row is on screen: count it. Answers the call that takes it off again. */
  register: (taskId: string) => () => void;
  refresh: () => void;
}

const CountsContext = createContext<TaskCounts | null>(null);

/**
 * The conversation counts for the task rows a view has on screen, in one request.
 *
 * A row reads its own badge from here rather than asking for it, because a
 * page of fifty tasks asking one at a time is fifty requests — the reason
 * `useThreadCounts` exists. **The rows say which they are**, by registering as
 * they render: a view's filters, folded done tasks and "Show more" all decide
 * what is on screen, and a second copy of those rules here would count tasks
 * nobody can see. A row outside a provider (a template, a test) shows no
 * conversation control at all.
 */
export function TaskConversationsProvider({ children }: { children: ReactNode }) {
  const mounted = useRef(new Map<string, number>());
  const [ids, setIds] = useState<string[]>([]);

  const sync = useCallback(() => {
    const next = [...mounted.current.keys()].sort();
    setIds((current) => (current.join(',') === next.join(',') ? current : next));
  }, []);

  const register = useCallback(
    (taskId: string) => {
      const rows = mounted.current;
      rows.set(taskId, (rows.get(taskId) ?? 0) + 1);
      sync();
      return () => {
        const left = (rows.get(taskId) ?? 1) - 1;
        if (left > 0) rows.set(taskId, left);
        else rows.delete(taskId);
        sync();
      };
    },
    [sync],
  );

  const { countFor, refetch } = useThreadCounts(TASK_THREAD_ENTITY, ids);
  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const value = useMemo(() => ({ countFor, register, refresh }), [countFor, register, refresh]);
  return <CountsContext.Provider value={value}>{children}</CountsContext.Provider>;
}

/** A task row's conversation icon, with its unread badge. */
export function TaskConversationButton({ task }: { task: ChecklistItem }) {
  const counts = useContext(CountsContext);
  const register = counts?.register;
  useEffect(() => register?.(task.id), [register, task.id]);
  if (!counts) return null;
  return (
    <RecordConversations
      entityType={TASK_THREAD_ENTITY}
      entityId={task.id}
      variant="icon"
      count={counts.countFor(task.id)}
      onActivity={counts.refresh}
    />
  );
}
